import React, { useCallback } from 'react'

// Components
import VerificationCodeInput from 'components/VerificationCode'
import Panel from 'components/Panel'

// Local Components
import HelpNote from './HelpNote'

// Styles
import {
  Title,
  FormContainer,
  Note,
  StyledLink,
  Description,
  Paragraph,
} from '../SignInToLinkAccountPage.styles'

// Types
import {
  ValidateMfaTotpMutation,
  useValidateMfaTotpMutation,
} from '@sketch/gql-types'
import { ParsedError } from '@sketch/graphql-apollo/useMutation'

interface VerificationCodeProps {
  onCompleted?: ((data: ValidateMfaTotpMutation) => void) | undefined
  onError: (error: ParsedError) => void
  toggleInterface: () => void
  onVerificationCodeFilled: () => void
  mfaToken: string
  verificationError?: string
}

/**
 * Renders the UI that allows the user to enter the verification code
 * This only renders if the user has mfaState === 'ENABLED' which is
 * returned after the user Signs in
 */
const VerificationCode: React.FC<VerificationCodeProps> = ({
  onCompleted,
  onError,
  onVerificationCodeFilled,
  toggleInterface,
  mfaToken,
  verificationError,
}) => {
  // Verification Code Validation
  const [validateMfaTotp] = useValidateMfaTotpMutation({
    onCompleted,
    onError,
  })

  const handleVerificationCodeFilled = useCallback(
    (code: string) => {
      onVerificationCodeFilled()

      // Code from the Authenticator App
      validateMfaTotp({
        variables: {
          token: mfaToken,
          totp: code,
        },
      })
    },
    [mfaToken, validateMfaTotp, onVerificationCodeFilled]
  )

  return (
    <>
      <Title>Enter Verification Code</Title>
      <Panel.Body>
        <Description>
          Open your authenticator app and enter the code before it expires — or
          wait for a new one.
        </Description>
        <FormContainer>
          <VerificationCodeInput
            onFilled={handleVerificationCodeFilled}
            error={verificationError}
          />
          <Note>
            <Paragraph>
              Can’t access your authenticator app?{' '}
              <StyledLink
                variant="primary"
                onClick={toggleInterface}
                isUnderlined
                external
              >
                Use a recovery code
              </StyledLink>
              <br />
              <HelpNote />
            </Paragraph>
          </Note>
        </FormContainer>
      </Panel.Body>
    </>
  )
}

export default VerificationCode
